import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash2, Star, CheckCircle, XCircle, Settings, Instagram, MessageCircle, User, Calendar, Zap, Facebook, Edit2 } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { instancesAPI, fluxosAPI, iaAPI, cadastroAPI } from '../services/api';
import { useCadastro } from '../contexts/CadastroContext';
import { useTheme } from '../contexts/ThemeContext';
import { useInstances } from '../contexts/InstancesContext';
import { useConfirm } from '../hooks/useConfirm';
import MetaApiConfig from '../components/MetaApiConfig';
import ZApiConfig from '../components/ZApiConfig';

import EmbeddedSignup from '../components/EmbeddedSignup';
import UsageBar from '../components/UsageBar';
import WhatsAppProfileModal from '../components/WhatsAppProfileModal';
import ValoresPorCanal from '../components/FluxoEditor/ValoresPorCanal';

const InstancesManagement = () => {
  const { darkMode } = useTheme();
  const { refreshInstances } = useInstances();
  const showConfirm = useConfirm();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [newInstanceName, setNewInstanceName] = useState('');
  const [creating, setCreating] = useState(false);
  const [fluxos, setFluxos] = useState([]);
  const [selectedFluxo, setSelectedFluxo] = useState(null);
  // Os valores por canal ficam aqui para o botao principal de salvar
  // grava-los junto — antes o componente os guardava e o save os descartava.
  const [valoresCanal, setValoresCanal] = useState({});
  const [iaConfigs, setIaConfigs] = useState([]);
  const [selectedIaConfig, setSelectedIaConfig] = useState(null);

  const [fluxosAtivos, setFluxosAtivos] = useState(false);
  const [showEmbeddedSignup, setShowEmbeddedSignup] = useState(false);
  const [embeddedMode, setEmbeddedMode] = useState(null); // null, 'normal', 'coexistence'
  const [profileModalId, setProfileModalId] = useState(null);
  const [nomeCanal, setNomeCanal] = useState('');
  const [saving, setSaving] = useState(false);
  const [configTab, setConfigTab] = useState('configuracoes'); // 'configuracoes' ou 'conexao'
  const [selectedProvider, setSelectedProvider] = useState('meta-api'); // 'meta-api' ou 'z-api'
  const [showDisconnected, setShowDisconnected] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const clienteId = user.cliente_id;
  const { verificarEAbrir } = useCadastro();

  const verificarCadastroAntes = async (acao) => {
    const ok = await verificarEAbrir(clienteId);
    if (ok) acao();
  };

  useEffect(() => {
    if (clienteId) {
      loadInstances();
      loadFluxos();
      loadIaConfigs();
    } else {
      toast.error('Erro: Cliente não identificado. Faça login novamente.');
      setLoading(false);
    }
  }, [clienteId]);

  const loadFluxos = async () => {
    try {
      const response = await fluxosAPI.list(clienteId);
      console.log('Fluxos carregados:', response.data);
      const fluxosData = response.data?.data || response.data || [];
      setFluxos(Array.isArray(fluxosData) ? fluxosData : []);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
      setFluxos([]);
    }
  };

  const loadIaConfigs = async () => {
    try {
      const response = await iaAPI.listConfigs(clienteId);
      const configs = response.data?.data || response.data || [];
      setIaConfigs(Array.isArray(configs) ? configs : []);
    } catch (error) {
      console.error('Erro ao carregar configs IA:', error);
      setIaConfigs([]);
    }
  };

  const loadInstances = async () => {
    if (!clienteId) return;

    try {
      setLoading(true);
      const response = await instancesAPI.list(clienteId);
      setInstances(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar instâncias');
      console.error('Erro ao carregar instâncias:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!clienteId) {
      toast.error('Erro: Cliente não identificado');
      return;
    }

    if (!selectedPlatform) {
      toast.error('Selecione uma plataforma');
      return;
    }

    if (!newInstanceName.trim()) {
      toast.error('Digite um nome para o canal');
      return;
    }

    setCreating(true);
    try {
      const response = await instancesAPI.create({
        cliente_id: clienteId,
        usuario_id: user.id,
        nome_instancia: newInstanceName,
        platform: selectedPlatform,
      });

      console.log('Canal criado:', response.data);

      toast.success('Canal criado com sucesso!');
      setShowCreateModal(false);
      setSelectedPlatform(null);
      setNewInstanceName('');

      // Buscar instância completa com todas as relações
      const instanceId = response.data.data.id; // Corrigido: data.data.id
      console.log('Buscando instância:', instanceId);

      await loadInstances();
      refreshInstances(); // Atualizar contexto

      // Buscar da API para ter todos os dados
      const fullInstanceResponse = await instancesAPI.list(clienteId);
      console.log('Instâncias carregadas:', fullInstanceResponse.data.length);

      const fullInstance = fullInstanceResponse.data.find(i => i.id === instanceId);
      console.log('Instância encontrada:', fullInstance);

      if (fullInstance) {
        setSelectedInstance(fullInstance);
        setNomeCanal(fullInstance.nome_instancia);
        setSelectedFluxo(fullInstance.fluxo_padrao_id || null);
        setSelectedIaConfig(fullInstance.ia_config_id || null);
        setFluxosAtivos(fullInstance.regras_automaticas_ativas || false);
        setConfigTab('conexao');
        setShowConfigModal(true);
        console.log('Modal aberto');
      } else {
        console.error('Instância não encontrada na lista');
      }
    } catch (error) {
      toast.error('Erro ao criar canal');
      console.error('Erro ao criar canal:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleSetDefault = async (instanceId) => {
    try {
      await instancesAPI.setDefault(instanceId, clienteId);
      toast.success('Instância padrão definida!');
      loadInstances();
    } catch (error) {
      toast.error('Erro ao definir instância padrão');
    }
  };

  const handleDelete = async (instanceId, instanceName) => {
    showConfirm({
      title: 'Deletar Instância',
      message: `Tem certeza que deseja deletar a instância "${instanceName}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Deletar',
      type: 'danger',
      onConfirm: async () => {
        try {
          await instancesAPI.delete(instanceId, clienteId);
          toast.success('Instância deletada!');
          loadInstances();
          refreshInstances();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Erro ao deletar instância');
        }
      }
    });
  };

  const handleConfigure = (instance) => {
    setSelectedInstance(instance);
    setNomeCanal(instance.nome_instancia);
    setSelectedFluxo(instance.fluxo_padrao_id || null);
    setValoresCanal({});
    setSelectedIaConfig(instance.ia_config_id || null);
    setFluxosAtivos(instance.regras_automaticas_ativas || false);
    setSelectedProvider(instance.provider || 'meta-api');
    setConfigTab('configuracoes');
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      // Salvar nome, IA e fluxos
      await instancesAPI.updateConfiguracoes(selectedInstance.id, {
        nome_instancia: nomeCanal,
        ia_config_id: selectedIaConfig,
        regras_automaticas_ativas: fluxosAtivos,
      });

      // Salvar fluxo específico
      if (fluxosAtivos) {
        await instancesAPI.setFluxo(selectedInstance.id, selectedFluxo);
        // Os valores dos parâmetros vão no MESMO save: um botão separado dentro
        // do modal fazia o usuário perdê-los sem aviso ao clicar em Salvar.
        if (selectedFluxo) {
          await fluxosAPI.setValores(selectedFluxo, 'instancia', selectedInstance.id, valoresCanal);
        }
      }

      toast.success('Configurações salvas!');
      loadInstances();
      setShowConfigModal(false);
      setSelectedInstance(null);
    } catch (error) {
      toast.error('Erro ao salvar configurações');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="Gerenciar Canais" subtitle="Configure WhatsApp, Instagram e outros canais de comunicação">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={() => verificarCadastroAntes(() => setShowEmbeddedSignup(true))}
              className="flex items-center space-x-1 sm:space-x-2 px-2 py-1.5 sm:px-4 sm:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="hidden sm:inline">Conectar WhatsApp</span>
            </button>
            <button
              onClick={() => verificarCadastroAntes(() => window.location.href = `${process.env.REACT_APP_API_URL}/meta-auth/connect?cliente_id=${clienteId}&platform=instagram`)}
              className="flex items-center space-x-1 sm:space-x-2 px-2 py-1.5 sm:px-4 sm:py-2 bg-nomoz-orange hover:bg-nomoz-orange-dark text-white rounded-lg transition-colors"
            >
              <Instagram className="w-5 h-5" />
              <span className="hidden sm:inline">Conectar Instagram/Facebook</span>
            </button>
            <button
              onClick={() => verificarCadastroAntes(() => {
                setSelectedInstance(null);
                setShowCreateModal(true);
              })}
              className="flex items-center space-x-1 sm:space-x-2 px-2 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Canal Manual</span>
            </button>
          </div>
        </PageHeader>

        <div className={`${darkMode ? 'bg-zinc-900' : 'bg-gray-50'} rounded-xl p-6`}>

          {/* Filtro de desconectados */}
          <div className="flex items-center justify-end mb-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showDisconnected}
                onChange={(e) => setShowDisconnected(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-nomoz-orange focus:ring-nomoz-orange"
              />
              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Mostrar desconectados ({instances.filter(i => i.status !== 'conectada' || !i.ativo).length})
              </span>
            </label>
          </div>

          {/* Lista de Instâncias */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nomoz-orange mx-auto"></div>
            </div>
          ) : instances.length === 0 ? (
            <div className={`text-center py-12 rounded-lg ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Nenhum canal cadastrado. Clique em "Adicionar Canal" para começar.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Canais Conectados */}
              {instances.sort(i => i.provider == 'meta-api' ? -1 : 0)
                .filter(i => i.status === 'conectada' && i.ativo).length > 0 && (
                  <div>
                    <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                      <CheckCircle className="w-4 h-4" />
                      Conectados ({instances.filter(i => i.status === 'conectada' && i.ativo).length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {instances
                        .filter((instance) => instance.status === 'conectada' && instance.ativo)
                        .map((instance) => (
                          <div
                            key={instance.id}
                            className={`rounded-xl p-6 border-2 transition-all shadow-lg flex flex-col ${instance.instancia_padrao
                                ? darkMode
                                  ? 'border-yellow-500 bg-gradient-to-br from-yellow-900/20 to-zinc-800'
                                  : 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-white'
                                : darkMode
                                  ? 'border-gray-700 bg-zinc-800 hover:border-gray-600'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                          >
                            {/* Header com Logo da Plataforma */}
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                {/* Logo WhatsApp/Instagram/Facebook */}
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${instance.platform === 'instagram'
                                    ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                                    : instance.platform === 'facebook'
                                      ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                                      : 'bg-gradient-to-br from-green-500 to-green-600'
                                  }`}>
                                  {instance.platform === 'instagram' ? (
                                    <Instagram className="w-6 h-6 text-white" />
                                  ) : instance.platform === 'facebook' ? (
                                    <Facebook className="w-6 h-6 text-white" />
                                  ) : (
                                    <MessageCircle className="w-6 h-6 text-white" />
                                  )}
                                </div>

                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                      {instance.nome_instancia}
                                    </h3>
                                    {instance.instancia_padrao && (
                                      <Star className="w-5 h-5 text-yellow-500 fill-current" />
                                    )}
                                  </div>
                                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {instance.platform === 'instagram' ? `@${instance.instagram_username || instance.nome_instancia}` : (instance.numero_whatsapp || 'Não configurado')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5">
                                {instance.platform === 'whatsapp' && instance.phone_number_id && (
                                  <button
                                    onClick={() => setProfileModalId(instance.id)}
                                    className={`p-1 rounded hover:bg-black/10 ${darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-400 hover:text-gray-700"}`}
                                    title="Editar perfil WhatsApp"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                )}
                                {instance.status === 'conectada' ? (
                                  <CheckCircle className="w-6 h-6 text-green-500" />
                                ) : (
                                  <XCircle className="w-6 h-6 text-gray-400" />
                                )}
                              </div>
                            </div>

                            {/* Informações do Canal */}
                            <div className={`space-y-3 mb-4 p-4 rounded-lg ${darkMode ? 'bg-zinc-900/50' : 'bg-gray-50'}`}>
                              {/* Quality / Tier / Uso — só WhatsApp */}
                              {instance.platform === 'whatsapp' && instance.phone_number_id && (
                                <UsageBar instanceId={instance.id} />
                              )}

                              {/* Status */}
                              <div className="flex items-center justify-between">
                                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status:</span>
                                <span
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${instance.status === 'conectada'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                                    }`}
                                >
                                  {instance.status === 'conectada' ? 'Conectado' : 'Desconectado'}
                                </span>
                              </div>

                              {/* Criado por */}
                              {instance.usuario && (
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    <User className="w-4 h-4 mr-1" />
                                    Criado por:
                                  </span>
                                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {instance.usuario.nome || 'N/A'}
                                  </span>
                                </div>
                              )}

                              {/* ID Meta */}
                              {(instance.phone_number_id || instance.instagram_account_id) && (
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    <span className="mr-1">🔑</span>
                                    ID Meta:
                                  </span>
                                  <span className={`text-xs font-mono ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {instance.phone_number_id || instance.instagram_account_id}
                                  </span>
                                </div>
                              )}

                              {/* Data de criação */}
                              {instance.create_at && (
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    <Calendar className="w-4 h-4 mr-1" />
                                    Criado em:
                                  </span>
                                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {new Date(instance.create_at).toLocaleDateString('pt-BR')}
                                  </span>
                                </div>
                              )}

                              {/* IA Ativa */}
                              <div className="flex items-center justify-between">
                                <span className={`text-sm flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  <Zap className="w-4 h-4 mr-1" />
                                  IA Ativa:
                                </span>
                                <span className={`text-sm font-semibold ${instance.ia_config_id ? 'text-green-500' : 'text-gray-500'}`}>
                                  {instance.ia_config_id ? 'Sim' : 'Não'}
                                </span>
                              </div>

                              {/* Fluxo Ativo */}
                              <div className="flex items-center justify-between">
                                <span className={`text-sm flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  <Zap className="w-4 h-4 mr-1" />
                                  Fluxo:
                                </span>
                                {instance.regras_automaticas_ativas && instance.fluxo_padrao ? (
                                  <span className={`text-sm font-semibold text-green-500`}>
                                    {instance.fluxo_padrao.nome}
                                  </span>
                                ) : (
                                  <span className={`text-sm font-semibold text-gray-500`}>
                                    {instance.regras_automaticas_ativas ? 'Nenhum' : 'Desativado'}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Ações */}
                            <div className="flex space-x-2 mt-auto pt-4">
                              <button
                                onClick={() => handleConfigure(instance)}
                                className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                                  }`}
                              >
                                <Settings className="w-4 h-4" />
                                <span>Configurar</span>
                              </button>

                              {!instance.instancia_padrao && (
                                <button
                                  onClick={() => handleSetDefault(instance.id)}
                                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode
                                      ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                      : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                                    }`}
                                  title="Definir como padrão"
                                >
                                  <Star className="w-4 h-4" />
                                </button>
                              )}

                              {instances.length > 1 && (
                                <button
                                  onClick={() => handleDelete(instance.id, instance.nome_instancia)}
                                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode
                                      ? 'bg-red-600 hover:bg-red-700 text-white'
                                      : 'bg-red-500 hover:bg-red-600 text-white'
                                    }`}
                                  title="Deletar"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

              {/* Canais Desconectados */}
              {showDisconnected && instances.filter(i => i.status !== 'conectada' || !i.ativo).length > 0 && (
                <div>
                  <div className={`border-t ${darkMode ? 'border-zinc-700' : 'border-gray-200'} pt-6`}>
                    <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 flex items-center gap-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      <XCircle className="w-4 h-4" />
                      Desconectados ({instances.filter(i => i.status !== 'conectada' || !i.ativo).length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-70">
                      {instances
                        .filter((instance) => instance.status !== 'conectada' || !instance.ativo)
                        .map((instance) => (
                          <div
                            key={instance.id}
                            className={`rounded-xl p-6 border-2 transition-all shadow-lg flex flex-col ${darkMode
                                ? 'border-gray-700 bg-zinc-800 hover:border-gray-600'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                          >
                            {/* Header com Logo da Plataforma */}
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${instance.platform === 'instagram'
                                    ? 'bg-gradient-to-br from-purple-500/50 to-pink-500/50'
                                    : instance.platform === 'facebook'
                                      ? 'bg-gradient-to-br from-blue-500/50 to-blue-600/50'
                                      : 'bg-gradient-to-br from-green-500/50 to-green-600/50'
                                  }`}>
                                  {instance.platform === 'instagram' ? (
                                    <Instagram className="w-6 h-6 text-white" />
                                  ) : instance.platform === 'facebook' ? (
                                    <Facebook className="w-6 h-6 text-white" />
                                  ) : (
                                    <MessageCircle className="w-6 h-6 text-white" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <h3 className={`font-bold text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {instance.nome_instancia}
                                  </h3>
                                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                    {instance.platform === 'instagram' ? `@${instance.instagram_username || instance.nome_instancia}` : (instance.numero_whatsapp || 'Não configurado')}
                                  </p>
                                </div>
                              </div>
                              <XCircle className="w-6 h-6 text-gray-400" />
                            </div>

                            {/* Status */}
                            <div className={`p-3 rounded-lg mb-4 ${darkMode ? 'bg-zinc-900/50' : 'bg-gray-50'}`}>
                              <div className="flex items-center justify-between">
                                <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Status:</span>
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400">
                                  Desconectado
                                </span>
                              </div>
                            </div>

                            {/* Ações */}
                            {/* Ações */}
                            <div className="flex space-x-2 mt-auto pt-4">
                              <button
                                onClick={() => handleConfigure(instance)}
                                className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                                  }`}
                              >
                                <Settings className="w-4 h-4" />
                                <span>Reconectar</span>
                              </button>
                              {instances.length > 1 && (
                                <button
                                  onClick={() => handleDelete(instance.id, instance.nome_instancia)}
                                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode
                                      ? 'bg-red-600 hover:bg-red-700 text-white'
                                      : 'bg-red-500 hover:bg-red-600 text-white'
                                    }`}
                                  title="Deletar"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal Criar Canal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg shadow-xl max-w-md w-full ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Adicionar Novo Canal
              </h3>

              {/* Seleção de Plataforma */}
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Selecione a Plataforma
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setSelectedPlatform('whatsapp')}
                    className={`p-4 rounded-lg border-2 transition-all ${selectedPlatform === 'whatsapp'
                        ? 'border-nomoz-orange bg-green-50 dark:bg-green-900/20'
                        : darkMode
                          ? 'border-gray-600 hover:border-gray-500'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-white" />
                      </div>
                      <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        WhatsApp
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => { setSelectedPlatform('instagram'); setSelectedProvider('meta-api'); }}
                    className={`p-4 rounded-lg border-2 transition-all ${selectedPlatform === 'instagram'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : darkMode
                          ? 'border-gray-600 hover:border-gray-500'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Instagram className="w-6 h-6 text-white" />
                      </div>
                      <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Instagram
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Nome do Canal */}
              {selectedPlatform && (
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Nome do Canal
                  </label>
                  <input
                    type="text"
                    value={newInstanceName}
                    onChange={(e) => setNewInstanceName(e.target.value)}
                    placeholder={selectedPlatform === 'whatsapp' ? 'Ex: WhatsApp Vendas' : 'Ex: Instagram Loja'}
                    className={`w-full px-3 py-2 rounded-lg border ${darkMode
                        ? 'bg-zinc-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                      } focus:outline-none focus:ring-2 ${selectedPlatform === 'whatsapp' ? 'focus:ring-nomoz-orange' : 'focus:ring-purple-500'
                      }`}
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedPlatform(null);
                    setNewInstanceName('');
                  }}
                  className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                    }`}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating || !selectedPlatform}
                  className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${selectedPlatform === 'whatsapp'
                      ? 'bg-nomoz-orange hover:bg-nomoz-orange-dark'
                      : selectedPlatform === 'instagram'
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                        : 'bg-gray-400'
                    }`}
                >
                  {creating ? 'Criando...' : 'Criar Canal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar WhatsApp */}

      {/* Modal Configurar */}
      {showConfigModal && selectedInstance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`rounded-lg shadow-xl max-w-3xl w-full my-8 ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Configurar: {selectedInstance.nome_instancia}
                </h3>
                <button
                  onClick={() => {
                    setShowConfigModal(false);
                    setSelectedInstance(null);
                  }}
                  className={`text-gray-500 hover:text-gray-700 ${darkMode ? 'hover:text-gray-300' : ''}`}
                >
                  ✕
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-700 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setConfigTab('configuracoes')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${configTab === 'configuracoes'
                        ? 'border-nomoz-orange text-green-500'
                        : darkMode
                          ? 'border-transparent text-gray-400 hover:text-gray-300'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    ⚙️ Configurações
                  </button>
                  <button
                    onClick={() => setConfigTab('conexao')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${configTab === 'conexao'
                        ? 'border-nomoz-orange text-green-500'
                        : darkMode
                          ? 'border-transparent text-gray-400 hover:text-gray-300'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    🔗 Conexão
                  </button>
                </nav>
              </div>

              {/* Conteúdo das Abas */}
              {configTab === 'configuracoes' ? (
                <div className="space-y-6">
                  {/* Nome da Instância */}
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Nome do Canal
                    </label>
                    <input
                      type="text"
                      value={nomeCanal}
                      onChange={(e) => setNomeCanal(e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg border ${darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      placeholder="Ex: WhatsApp Vendas"
                    />
                  </div>

                  {/* Seleção de Automação */}
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}>
                    <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Tipo de Automação
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="automacao"
                          checked={!selectedIaConfig && !fluxosAtivos}
                          onChange={() => {
                            setSelectedIaConfig(null);
                            setFluxosAtivos(false);
                          }}
                          className="w-4 h-4 text-green-600"
                        />
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Nenhuma (Apenas Atendimento Humano)
                          </p>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Todas as mensagens vão direto para atendimento manual
                          </p>
                        </div>
                      </label>

                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="automacao"
                          checked={fluxosAtivos}
                          onChange={() => {
                            setSelectedIaConfig(null);
                            setFluxosAtivos(true);
                          }}
                          className="w-4 h-4 text-green-600"
                        />
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Fluxos de Conversa
                          </p>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Menus estruturados e fluxos automáticos
                          </p>
                        </div>
                      </label>

                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="automacao"
                          checked={!!selectedIaConfig}
                          onChange={() => {
                            setSelectedIaConfig(iaConfigs[0]?.id || null);
                            setFluxosAtivos(false);
                          }}
                          className="w-4 h-4 text-green-600"
                        />
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Nomoz Agent (IA)
                          </p>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Respostas automáticas com inteligência artificial
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Seletor de Config IA - Aparece apenas se IA estiver ativa */}
                  {selectedIaConfig && (
                    <div className={`p-4 rounded-lg border-2 border-nomoz-orange ${darkMode ? 'bg-zinc-900' : 'bg-green-50'}`}>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Assistente IA
                      </label>
                      <select
                        value={selectedIaConfig || ''}
                        onChange={(e) => setSelectedIaConfig(e.target.value || null)}
                        className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      >
                        <option value="">Nenhum</option>
                        {iaConfigs.map(config => (
                          <option key={config.id} value={config.id}>
                            {config.nome}
                          </option>
                        ))}
                      </select>
                      {iaConfigs.length === 0 && (
                        <p className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Nenhum assistente configurado. Crie um em Configurações → Nomoz Agent.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Seletor de Fluxo - Aparece apenas se Fluxos estiver ativo */}
                  {fluxosAtivos && (
                    <div className={`p-4 rounded-lg border-2 border-nomoz-orange ${darkMode ? 'bg-zinc-900' : 'bg-green-50'}`}>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Selecione o Fluxo
                      </label>
                      <select
                        value={selectedFluxo || ''}
                        onChange={(e) => setSelectedFluxo(e.target.value || null)}
                        className={`w-full px-3 py-2 rounded-lg border ${darkMode
                            ? 'bg-zinc-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                          }`}
                      >
                        <option value="">Usar fluxo padrão do cliente</option>
                        {Array.isArray(fluxos) && fluxos.filter(f => f.ativo).map((fluxo) => (
                          <option key={fluxo.id} value={fluxo.id}>
                            {fluxo.nome}
                          </option>
                        ))}
                      </select>
                      <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {selectedFluxo
                          ? `Fluxo selecionado: ${fluxos.find(f => f.id === selectedFluxo)?.nome}`
                          : 'Usando fluxo padrão do cliente'}
                      </p>

                      {/* Os valores ficam no canal, não no fluxo: é o que
                          permite 3 unidades usarem o MESMO fluxo. */}
                      <ValoresPorCanal
                        fluxoId={selectedFluxo}
                        instanciaId={selectedInstance?.id}
                        darkMode={darkMode}
                        valores={valoresCanal}
                        onChange={setValoresCanal}
                      />
                    </div>
                  )}

                  {/* Botão Salvar Configurações */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
                    <button
                      onClick={() => {
                        setShowConfigModal(false);
                        setSelectedInstance(null);
                      }}
                      className={`px-6 py-2 rounded-lg font-medium ${darkMode
                          ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                        }`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSaveConfig}
                      disabled={saving}
                      className="px-6 py-2 bg-nomoz-orange hover:bg-nomoz-orange-dark text-white rounded-lg font-medium disabled:opacity-50"
                    >
                      {saving ? 'Salvando...' : 'Salvar Configurações'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Aba Conexão */
                <div className="space-y-6">
                  {/* Seleção de Provider - só pra WhatsApp */}
                  {(selectedInstance?.platform || selectedPlatform) !== 'instagram' && (
                    <div>
                      <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Selecione o Provider
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        {/* Meta API */}
                        <button
                          onClick={() => setSelectedProvider('meta-api')}
                          className={`p-4 rounded-lg border-2 transition-all ${selectedProvider === 'meta-api'
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
                            }`}
                        >
                          <div className="text-center">
                            <div className="text-3xl mb-2">🌐</div>
                            <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              Meta API
                            </div>
                            <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Oficial (WhatsApp/Instagram)
                            </div>
                          </div>
                        </button>

                        {/* Z-API */}
                        <button
                          onClick={() => setSelectedProvider('z-api')}
                          className={`p-4 rounded-lg border-2 transition-all ${selectedProvider === 'z-api'
                              ? 'border-nomoz-orange bg-green-50 dark:bg-green-900/20'
                              : 'border-gray-300 dark:border-gray-600 hover:border-green-300'
                            }`}
                        >
                          <div className="text-center">
                            <div className="text-3xl mb-2">⚡</div>
                            <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              Z-API
                            </div>
                            <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Não oficial (WhatsApp)
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Configuração Meta API */}
                  {selectedProvider === 'meta-api' && (
                    <MetaApiConfig
                      clienteId={clienteId}
                      instance={selectedInstance}
                      onConfigSaved={() => {
                        loadInstances();
                        toast.success('Conexão configurada!');
                      }}
                    />
                  )}

                  {/* Configuração Z-API */}
                  {selectedProvider === 'z-api' && (
                    <ZApiConfig
                      instance={selectedInstance}
                      onConfigSaved={() => {
                        loadInstances();
                        toast.success('Z-API configurada!');
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Embedded Signup WhatsApp */}
      {showEmbeddedSignup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${darkMode ? 'bg-zinc-800' : 'bg-white'} rounded-xl p-6 max-w-lg w-full mx-4`}>
            {!embeddedMode ? (
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Como deseja conectar seu WhatsApp?
                </h3>
                <button
                  onClick={() => setEmbeddedMode('normal')}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${darkMode ? 'border-gray-600 hover:border-green-500 bg-zinc-700' : 'border-gray-200 hover:border-green-500 bg-gray-50'}`}
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">🆕</span>
                    <div>
                      <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Número novo / Migrar para API</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Seu número será usado exclusivamente pela API. O app do celular será desconectado.
                      </p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setEmbeddedMode('coexistence')}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${darkMode ? 'border-gray-600 hover:border-blue-500 bg-zinc-700' : 'border-gray-200 hover:border-blue-500 bg-gray-50'}`}
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">📱</span>
                    <div>
                      <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Manter o App + API (Coexistência)</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Continue usando o WhatsApp Business App no celular E use a API para automações e campanhas.
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                        ⚠️ Limite: 20 msg/seg • App precisa ser aberto 1x a cada 14 dias
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            ) : (
              <EmbeddedSignup
                clienteId={clienteId}
                usuarioId={user.id}
                mode={embeddedMode}
                onSuccess={() => {
                  setShowEmbeddedSignup(false);
                  setEmbeddedMode(null);
                  loadInstances();
                }}
                onClose={() => { setShowEmbeddedSignup(false); setEmbeddedMode(null); }}
              />
            )}
            <button
              onClick={() => { setShowEmbeddedSignup(false); setEmbeddedMode(null); }}
              className={`w-full mt-4 py-2 rounded-lg ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {profileModalId && (
        <WhatsAppProfileModal
          instanceId={profileModalId}
          onClose={() => setProfileModalId(null)}
        />
      )}
    </Layout>
  );
};

export default InstancesManagement;
